import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MapPin, RefreshCw, ShieldCheck, AlertTriangle, Zap, Battery, Activity, Gauge, Info,
} from 'lucide-react';
import { Button } from './ui/button';
import { LoadingSpinner } from './UIComponents';
import { liveMonitoringAPI, alarmsAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
];

const fmtIST = (iso, withDate = true) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    ...(withDate ? { day: '2-digit', month: 'short', year: 'numeric' } : {}),
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
  }) + ' IST';
};

const fmtDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
  });
};

const fmtMinutes = (min) => {
  if (min == null || isNaN(min)) return '—';
  if (min < 60) return `${min.toFixed(min < 10 ? 1 : 0)} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${Math.round(min % 60)}m`;
};

const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)} min ago`;
  if (secs < 172800) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
};

const num = (v, d = 1) => (v == null || isNaN(v) ? null : Number(v).toFixed(d));

/* ── Status band ───────────────────────────────────────────── */
function StatusBand({ summary, onRefresh, refreshing }) {
  const { site, status, kpis } = summary;
  const tone = status.tone;
  const toneCls = tone === 'online'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : tone === 'outage'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';
  const dotCls = tone === 'online' ? 'bg-emerald-500' : tone === 'outage' ? 'bg-rose-500' : 'bg-slate-400';

  const meta = [
    { k: 'Installed Power', v: kpiUnit(site.installed_power_kw, 'kW') },
    { k: 'Installed Capacity', v: kpiUnit(site.installed_capacity_kwh, 'kWh') },
    { k: 'BESS Assets', v: site.bess_asset_count ?? '—' },
    { k: 'Commissioned', v: site.commissioned_date ? fmtDay(`${site.commissioned_date}T00:00:00Z`) : 'Not set' },
  ];

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-6 flex flex-wrap items-start justify-between gap-5"
      data-testid="live-status-band"
    >
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-slate-900" data-testid="live-site-title">
          {site.name} — Live Monitoring
        </h2>
        <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1 mb-5">
          <MapPin className="w-3.5 h-3.5" />
          {site.location || 'Location not set'}
          {site.internal_asset_ref ? ` · Asset: ${site.internal_asset_ref}` : ''}
        </p>
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          {meta.map((m) => (
            <div key={m.k}>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{m.k}</div>
              <div className="text-sm font-medium text-slate-900" data-testid={`meta-${m.k.toLowerCase().replace(/ /g, '-')}`}>
                {m.v}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-right space-y-2">
        <div
          className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold ${toneCls}`}
          data-testid="unified-system-status"
        >
          <span className={`w-2 h-2 rounded-full ${dotCls}`} />
          {status.label}
        </div>
        <div className="text-[11px] text-slate-500" data-testid="last-data-received">
          Last data received: {fmtIST(status.last_data_at)}
        </div>
        <div className="text-[10px] text-slate-400">Single status · source: {status.source}</div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="live-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
    </div>
  );
}

const kpiUnit = (v, unit) => (v == null ? '—' : `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })} ${unit}`);

/* ── Alarm strip ───────────────────────────────────────────── */
function AlarmStrip({ alarms }) {
  if (!alarms || alarms.length === 0) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm px-4 py-2.5 text-xs font-semibold flex items-center gap-2"
        data-testid="alarm-strip"
      >
        <ShieldCheck className="w-4 h-4" /> No active alarms
      </div>
    );
  }
  const critical = alarms.filter((a) => (a.severity || '').toLowerCase() === 'critical').length;
  return (
    <div
      className="bg-rose-50 border border-rose-200 text-rose-700 rounded-sm px-4 py-2.5 text-xs flex flex-wrap items-center gap-x-3 gap-y-1"
      data-testid="alarm-strip"
    >
      <span className="font-semibold flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        {alarms.length} active alarm{alarms.length > 1 ? 's' : ''}
        {critical > 0 ? ` · ${critical} critical` : ''}
      </span>
      {alarms.slice(0, 3).map((a) => (
        <span key={a.alarm_id} className="text-rose-600" data-testid={`alarm-chip-${a.alarm_type}`}>
          · {a.title || a.alarm_type}
        </span>
      ))}
    </div>
  );
}

/* ── KPI tiles ─────────────────────────────────────────────── */
function KpiTile({ label, pill, pillTone, value, unit, sub, icon: Icon, muted, testId }) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-sm p-4 ${muted ? 'opacity-60' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
        {pill ? (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pillTone}`}>{pill}</span>
        ) : (
          Icon && <Icon className="w-4 h-4 text-slate-400" />
        )}
      </div>
      <div className="text-2xl font-mono font-semibold text-slate-900 leading-none">
        {value}
        {unit && <span className="text-sm font-sans font-medium text-slate-500 ml-1">{unit}</span>}
      </div>
      <div className="text-[11px] text-slate-500 mt-2">{sub}</div>
    </div>
  );
}

function KpiRow({ kpis }) {
  const grid = kpis.grid || {};
  const gridConnected = grid.connected;
  const power = kpis.ac_active_power_kw;
  const dir = kpis.power_direction;
  const dirLabel = dir === 'discharging' ? '↓ Discharging' : dir === 'charging' ? '↑ Charging' : dir === 'idle' ? 'Idle' : 'No power data';
  const soc = kpis.soc_percent;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="live-kpi-row">
      <KpiTile
        testId="kpi-grid-connection"
        label="Grid Connection"
        pill={gridConnected == null ? 'NO DATA' : gridConnected ? 'ON' : 'OFF'}
        pillTone={gridConnected == null
          ? 'bg-slate-100 text-slate-500'
          : gridConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}
        value={gridConnected == null ? '—' : gridConnected ? 'Connected' : 'Outage'}
        sub={`Source: ${grid.source || 'unavailable'} · ${fmtAgo(grid.sampled_at)}`}
        icon={Zap}
      />
      <KpiTile
        testId="kpi-ac-power"
        label="AC Active Power"
        value={num(power, 1) ?? '—'}
        unit={power == null ? '' : 'kW'}
        sub={dirLabel}
        icon={Gauge}
      />
      <KpiTile
        testId="kpi-soc"
        label="Battery SOC"
        value={soc == null ? '—' : `${num(soc, 1)}%`}
        sub={soc == null ? 'Awaiting BMS SOC' : soc < 20 ? 'Low — below comfort floor' : 'Healthy range'}
        icon={Battery}
      />
      <KpiTile
        testId="kpi-soh"
        label="Battery SOH"
        pill={kpis.soh_pending ? 'Pending' : null}
        pillTone="bg-slate-100 text-slate-500"
        value={kpis.soh_pending ? '—' : `${num(kpis.soh_percent, 1)}%`}
        sub={kpis.soh_pending ? 'Pending — awaiting BMS data' : 'Reported by BMS'}
        muted={kpis.soh_pending}
        icon={Activity}
      />
      <KpiTile
        testId="kpi-cumulative-discharged"
        label="Cumulative Discharged"
        value={kpis.cumulative_discharge_kwh == null
          ? '—'
          : Number(kpis.cumulative_discharge_kwh).toLocaleString('en-IN', { maximumFractionDigits: 1 })}
        unit={kpis.cumulative_discharge_kwh == null ? '' : 'kWh'}
        sub="Since commissioning"
        icon={Battery}
      />
    </div>
  );
}

/* ── Trend chart ───────────────────────────────────────────── */
function TrendPanel({ siteId, range, setRange }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    liveMonitoringAPI.getTrend(siteId, range)
      .then((r) => { if (alive) setPoints(r.data?.points || []); })
      .catch(() => { if (alive) setPoints([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [siteId, range]);

  const data = useMemo(() => points.map((p) => ({
    t: new Date(p.t).toLocaleString('en-IN', {
      ...(range === 'today' ? {} : { day: '2-digit', month: 'short' }),
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
    }),
    power: p.power_kw,
    soc: p.soc_percent,
  })), [points, range]);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="trend-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Power &amp; SOC Trend</h3>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`trend-range-${r.key}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${range === r.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="h-[240px] flex items-center justify-center text-xs text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading trend…
        </div>
      ) : data.length === 0 ? (
        <div className="h-[240px] flex items-center justify-center text-xs text-slate-400" data-testid="trend-empty">
          No telemetry in this window.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
            <XAxis dataKey="t" stroke="#94a3b8" fontSize={10} interval="preserveStartEnd" minTickGap={40} />
            <YAxis yAxisId="p" stroke="#2f5bff" fontSize={10} />
            <YAxis yAxisId="s" orientation="right" domain={[0, 100]} stroke="#16a34a" fontSize={10} />
            <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 2, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="p" type="monotone" dataKey="power" name="AC Active Power (kW)"
              stroke="#2f5bff" strokeWidth={2} dot={false} connectNulls />
            <Line yAxisId="s" type="monotone" dataKey="soc" name="Battery SOC (%)"
              stroke="#16a34a" strokeWidth={2} dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      {range !== 'today' && (
        <p className="text-[10px] text-slate-400 mt-2">
          Hourly averages. For periods before live mirroring started, power is derived from the
          cumulative discharge counter.
        </p>
      )}
    </div>
  );
}

/* ── Unified event log ─────────────────────────────────────── */
const TYPE_TONE = {
  grid: 'bg-rose-50 text-rose-700',
  bess: 'bg-amber-50 text-amber-700',
  comms: 'bg-slate-100 text-slate-600',
  data: 'bg-sky-50 text-sky-700',
  momentary: 'bg-slate-100 text-slate-500',
  unverified: 'bg-amber-50 text-amber-800 border border-amber-300',
};

const fmtSecs = (s) => {
  if (s == null) return null;
  return s < 90 ? `${Number(s).toFixed(s < 10 ? 1 : 0)}s` : `${Math.round(s / 60)}m`;
};

/* BESS behaviour measured inside the outage window (backend-computed) */
function BessResponseLines({ e }) {
  if (e.category !== 'grid') return null;
  const responded = e.bess_responded;
  return (
    <div className="mt-1 space-y-0.5" data-testid={`bess-response-${e.event_id}`}>
      {responded === true && e.bess_response_time_seconds != null && (
        <div className="text-[10px] text-emerald-700">
          BESS responded in {fmtSecs(e.bess_response_time_seconds)}
          {e.bess_peak_discharge_kw != null ? ` · peak ${e.bess_peak_discharge_kw} kW` : ''}
        </div>
      )}
      {responded === true && e.bess_response_time_seconds == null && (
        <div className="text-[10px] text-emerald-700">
          BESS responded — energy delivered from the cumulative counter; response timing not sampled
        </div>
      )}
      {responded === false && (
        <div className="text-[10px] font-semibold text-rose-600">
          BESS did not respond{e.bess_response_basis === 'energy' ? ' — no discharge energy recorded in this window' : ''}
        </div>
      )}
      {responded == null && (
        <div className="text-[10px] text-slate-400">BESS response unknown — no battery data in window</div>
      )}
      {e.bess_stop_lag_seconds != null && (
        <div className="text-[10px] text-slate-500">
          BESS stopped discharging {fmtSecs(e.bess_stop_lag_seconds)} after restoration
        </div>
      )}
      {e.grid_status_confidence === 'unknown' && (
        <div className="text-[10px] text-amber-700">Grid status unknown — PCS feed was stale in this window</div>
      )}
    </div>
  );
}

function EventLog({ events, dieselPrice, completeness }) {
  const [filter, setFilter] = useState('faults');
  const shown = useMemo(() => (
    filter === 'all'
      ? events
      : filter === 'grid'
        ? events.filter((e) => e.category === 'grid' || e.category === 'unverified')
        : events.filter((e) => ['grid', 'unverified', 'bess'].includes(e.category))
  ), [events, filter]);
  const filters = [
    { key: 'faults', label: 'Outages & faults' },
    { key: 'grid', label: 'Grid outages only' },
    { key: 'all', label: 'All (incl. momentary, comms & data)' },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="event-log-panel">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          Outage &amp; Event Log
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
            Unified — grid + BESS
          </span>
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1.5">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`event-filter-${f.key}`}
                className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${filter === f.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-slate-500">
            {shown.length} event{shown.length === 1 ? '' : 's'}
            {dieselPrice ? ` · diesel ₹${dieselPrice}/L` : ''}
          </span>
        </div>
      </div>
      {completeness && !completeness.period_verified && (
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800 flex items-start gap-2"
          data-testid="completeness-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            <b>Outage count may be incomplete</b> — only {completeness.completeness_pct}% of expected monitoring
            data was received{completeness.unverified_days?.length
              ? ` (${completeness.unverified_days.length} day(s) below ${completeness.threshold_pct}% completeness)`
              : ''}. Days without data are reported as <b>unverified</b>, never as zero outages.
          </span>
        </div>
      )}
      {shown.length === 0 ? (
        <div className="p-8 text-center text-xs text-slate-400" data-testid="event-log-empty">
          No grid outages or fault events recorded for this site yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <tr>
                {['Start', 'End', 'Duration', 'Type', 'SOC Drop', 'Battery Used', 'Diesel Avoided'].map((h) => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((e) => (
                <tr key={e.event_id} className="hover:bg-slate-50" data-testid={`event-row-${e.event_id}`}>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-900">{fmtIST(e.start)}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">
                    {e.status === 'ongoing' || !e.end
                      ? <span className="text-rose-600 font-semibold">ONGOING</span>
                      : fmtIST(e.end)}
                  </td>
                  <td className="px-4 py-2.5 font-mono">{fmtMinutes(e.duration_minutes)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold ${TYPE_TONE[e.category] || TYPE_TONE.bess}`}>
                      {e.type}
                    </span>
                    {e.confidence && (
                      <span className="ml-1.5 text-[10px] font-semibold text-amber-800">
                        {e.confidence === 'likely' ? 'Likely' : 'Possible'}
                      </span>
                    )}
                    {e.detail && (
                      <span className="block text-[10px] text-slate-400 mt-1 max-w-md" title={e.detail}>
                        {e.detail}
                      </span>
                    )}
                    <BessResponseLines e={e} />
                  </td>
                  <td className="px-4 py-2.5 font-mono">{e.soc_drop_pct != null ? `${e.soc_drop_pct.toFixed(1)}%` : '—'}</td>
                  <td className="px-4 py-2.5 font-mono">{e.battery_kwh_used != null ? `${e.battery_kwh_used.toFixed(1)} kWh` : '—'}</td>
                  <td className="px-4 py-2.5 font-mono">
                    {e.diesel_litres_avoided != null
                      ? `${e.diesel_litres_avoided.toFixed(1)} L · ₹${Number(e.diesel_cost_avoided_inr || 0).toLocaleString('en-IN')}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Main ──────────────────────────────────────────────────── */
export default function LiveMonitoringTab({ siteId }) {
  const [summary, setSummary] = useState(null);
  const [alarms, setAlarms] = useState([]);
  const [events, setEvents] = useState([]);
  const [dieselPrice, setDieselPrice] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [range, setRange] = useState('today');

  const fetchAll = useCallback(async () => {
    try {
      const [sumRes, alarmRes, evRes] = await Promise.all([
        liveMonitoringAPI.getSummary(siteId),
        alarmsAPI.getActive(siteId).catch(() => ({ data: { alarms: [] } })),
        liveMonitoringAPI.getEvents(siteId, 50),
      ]);
      setSummary(sumRes.data);
      const a = alarmRes.data;
      setAlarms(Array.isArray(a) ? a : (a?.alarms || []));
      setEvents(evRes.data?.events || []);
      setDieselPrice(evRes.data?.diesel_price_per_litre ?? null);
      setCompleteness(evRes.data?.completeness ?? null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load live monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [siteId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const it = setInterval(fetchAll, 60000);
    return () => clearInterval(it);
  }, [fetchAll]);

  if (loading) return <LoadingSpinner />;
  if (!summary) return null;

  const warn = summary.data_quality?.commissioning_warning;

  return (
    <div className="space-y-5" data-testid="live-monitoring-tab">
      <StatusBand
        summary={summary}
        refreshing={refreshing}
        onRefresh={() => { setRefreshing(true); fetchAll(); }}
      />

      {warn && (
        <div
          className={`rounded-sm border px-4 py-2.5 text-xs flex items-start gap-2 ${warn.level === 'warning'
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-slate-50 border-slate-200 text-slate-600'}`}
          data-testid="commissioning-warning"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <b>Data check — needs human confirmation:</b> {warn.message}
            {warn.commissioned_date && ` (recorded: ${fmtDay(`${warn.commissioned_date}T00:00:00Z`)})`}
          </span>
        </div>
      )}

      <AlarmStrip alarms={alarms} />
      <KpiRow kpis={summary.kpis} />
      <TrendPanel siteId={siteId} range={range} setRange={setRange} />
      <EventLog events={events} dieselPrice={dieselPrice} completeness={completeness} />
    </div>
  );
}
